import React, {useCallback, useRef} from 'react';
import AuthImage from './AuthImage';
import IconButton from './IconButton';
import {
    BookmarkIcon,
    ClockIcon,
    DisposalIcon,
    DropletsIcon,
    EllipsisVerticalIcon,
    ListIcon,
    ShirtIcon,
    TrashIcon,
} from './icons';
import {ItemLifecycleStatus, ItemPurpose, retirementLabel} from '../support/itemLifecycle';
import {pluralTimes} from '../utils/plural';
import {formatDate, parseApiDate} from '../utils/dateTime';

/** Вещь в списке каталога — ровно те поля, что отдаёт `GET /clothing/items`. */
export interface CatalogItem {
    id: string;
    name: string;
    categoryName: string | null;
    /** Идентификатор бренда — для ссылки-фильтра каталога (CTHS-258); старый кэш его не знает. */
    brandId?: string | null;
    brandName: string | null;
    sizeName: string | null;
    conditionName: string | null;
    colorNames: string | null;
    purchaseDate: string | null;
    firstPhotoUrl: string | null;
    thrownAway?: boolean;
    inTrashBin?: boolean;
    purpose?: ItemPurpose | null;
    lifecycleStatus?: ItemLifecycleStatus | null;
    totalWears?: number | null;
    totalWashes?: number | null;
    lastWornAt?: string | null;
    aiRatingOverall?: number | null;
    favorite?: boolean | null;
}

export interface ClothingItemCardProps {
    item: CatalogItem;
    /** Режим выделения включён — чекбоксы видны, клик по карточке отмечает, а не открывает. */
    selectionMode: boolean;
    selected: boolean;
    /** Вещь можно отметить: есть право на массовое действие и вещь не утилизирована. */
    selectable: boolean;
    menuOpen: boolean;
    canEdit: boolean;
    canManageListItems: boolean;
    canMoveToTrash: boolean;
    onToggleSelect: (itemId: string) => void;
    /** Долгое нажатие на мобиле — включает режим выделения и отмечает вещь. */
    onLongPress: (itemId: string) => void;
    onOpen: (event: React.MouseEvent, itemId: string) => void;
    onAuxOpen: (event: React.MouseEvent, itemId: string) => void;
    onToggleFavorite: (itemId: string, event: React.MouseEvent) => void;
    onToggleMenu: (itemId: string) => void;
    onCloseMenu: () => void;
    onNavigate: (itemId: string) => void;
    onWear: (itemId: string) => void;
    /** «Стирал» (CTHS-264): POST /clothing/items/{id}/wash, право EDIT_ITEMS. */
    onWash: (itemId: string) => void;
    onAddToList: (itemId: string) => void;
    onTrash: (itemId: string) => void;
    /** Клик по бренду — отбор каталога по этому бренду (CTHS-258). */
    onBrandClick: (brandId: string) => void;
}

/** Долгое нажатие — 500 мс: короче ловит обычный тап, длиннее воспринимается как «не сработало». */
const LONG_PRESS_MS = 500;

/**
 * «Неизвестный» бренд приходит из распознавания как обычное значение справочника, но
 * в карточке он занимает место настоящего бренда и ничего не сообщает (C10) — не печатаем.
 */
export function visibleBrand(brandName: string | null | undefined): string | null {
    const trimmed = (brandName ?? '').trim();
    if (!trimmed) return null;
    const lower = trimmed.toLowerCase();
    if (lower.startsWith('неизвестн') || lower === 'unknown') return null;
    return trimmed;
}

/** Границы давности носки (CTHS-266): свежая — до 14 дней, «стынет» — до 90, дальше — мёртвая. */
const RECENT_WEAR_DAYS = 14;
const STALE_WEAR_DAYS = 90;
const DAY_MS = 24 * 60 * 60 * 1000;

export interface WearRecency {
    /** Класс точки-индикатора: success / warning / error из Status scale styleguide. */
    dotClass: string;
    /** Подпись для `title` и доступного имени. */
    title: string;
}

/**
 * Давность последней носки для индикатора карточки.
 *
 * Пустой журнал — не «носили давно», а «записей нет»: подпись говорит именно это,
 * хотя цветом такая вещь попадает в ту же группу, что и забытая на год.
 */
export function wearRecency(lastWornAt: string | null | undefined, now: Date = new Date()): WearRecency {
    const parsed = parseApiDate(lastWornAt);
    if (!parsed) {
        return {dotClass: 'bg-red-500', title: 'Нет записей о носке'};
    }
    const days = Math.floor((now.getTime() - parsed.getTime()) / DAY_MS);
    const date = formatDate(lastWornAt);
    if (days <= RECENT_WEAR_DAYS) {
        return {dotClass: 'bg-brand-500', title: `Последняя носка: ${date}`};
    }
    if (days <= STALE_WEAR_DAYS) {
        return {dotClass: 'bg-amber-500', title: `Последняя носка: ${date}`};
    }
    return {dotClass: 'bg-red-500', title: `Последняя носка: ${date}`};
}

export function conditionDot(name: string | null): string {
    if (!name) return 'bg-gray-400';
    const n = name.toLowerCase();
    if (n.includes('отличн') || n.includes('хорош')) return 'bg-green-500';
    if (n.includes('удовлетвор')) return 'bg-yellow-400';
    return 'bg-red-500';
}

/**
 * Карточка вещи в каталоге.
 *
 * Вся карточка — одна ссылка (C1): фото занимает три четверти площади, и раньше оно
 * не открывало вещь. Ссылка обнимает фото и текст, а управляющие элементы (чекбокс,
 * закладка, ⋮) лежат рядом с ней, а не внутри: кнопка внутри ссылки — невалидная
 * разметка, и клик по ней проваливался бы в переход.
 */
const ClothingItemCard: React.FC<ClothingItemCardProps> = ({
    item,
    selectionMode,
    selected,
    selectable,
    menuOpen,
    canEdit,
    canManageListItems,
    canMoveToTrash,
    onToggleSelect,
    onLongPress,
    onOpen,
    onAuxOpen,
    onToggleFavorite,
    onToggleMenu,
    onCloseMenu,
    onNavigate,
    onWear,
    onWash,
    onAddToList,
    onTrash,
    onBrandClick,
}) => {
    const pressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Долгое нажатие уже включило выделение — следующий click по той же карточке
    // не должен уводить на вещь.
    const suppressClickRef = useRef(false);

    const cancelPress = useCallback(() => {
        if (pressTimerRef.current) {
            clearTimeout(pressTimerRef.current);
            pressTimerRef.current = null;
        }
    }, []);

    const startPress = useCallback(() => {
        if (selectionMode || !selectable) return;
        cancelPress();
        pressTimerRef.current = setTimeout(() => {
            pressTimerRef.current = null;
            suppressClickRef.current = true;
            onLongPress(item.id);
        }, LONG_PRESS_MS);
    }, [cancelPress, item.id, onLongPress, selectable, selectionMode]);

    const handleClick = (event: React.MouseEvent) => {
        if (suppressClickRef.current) {
            suppressClickRef.current = false;
            event.preventDefault();
            return;
        }
        // В режиме выделения карточка работает как большой чекбокс: попасть пальцем
        // в квадрат 44 px на каждой вещи подряд — работа, а не выбор.
        if (selectionMode && selectable) {
            event.preventDefault();
            onToggleSelect(item.id);
            return;
        }
        onOpen(event, item.id);
    };

    const brand = visibleBrand(item.brandName);
    const brandLinkId = brand && item.brandId ? item.brandId : null;
    const meta = [item.categoryName, brand, item.sizeName].filter(Boolean).join(' · ');
    // Части мета-строки в порядке показа; бренд помечен, потому что он кликабелен,
    // а совпасть по тексту с категорией или размером он вполне может.
    const metaParts: {text: string; isBrand: boolean}[] = [
        {text: item.categoryName ?? '', isBrand: false},
        {text: brand ?? '', isBrand: true},
        {text: item.sizeName ?? '', isBrand: false},
    ].filter(part => part.text !== '');
    const recency = wearRecency(item.lastWornAt);

    /**
     * Бренд ведёт в каталог этого бренда (CTHS-258). Внутри ссылки карточки живёт не
     * вложенный `<a>` (невалидная разметка), а элемент с ролью ссылки: клик гасится и
     * не проваливается в переход к вещи — та же техника, что у закладки и ⋮.
     */
    const handleBrandActivate = (event: React.SyntheticEvent) => {
        if (!brandLinkId) return;
        event.preventDefault();
        event.stopPropagation();
        onBrandClick(brandLinkId);
    };

    const brandLabel = (text: string) => (
        <span
            role="link"
            tabIndex={0}
            data-testid="item-card-brand"
            title={`Показать все вещи бренда «${text}»`}
            onClick={handleBrandActivate}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleBrandActivate(e); }}
            // `self-stretch` растягивает бренд на всю высоту мета-строки: на мобиле она
            // 44 px, и тач-зона получается полной без наложения на соседние строки.
            className="inline-flex items-center self-stretch truncate rounded px-1 -mx-1
                underline decoration-dotted underline-offset-2
                cursor-pointer hover:text-primary-600 dark:hover:text-primary-400
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
        >
            {text}
        </span>
    );

    return (
        <div
            data-testid="item-card"
            onTouchStart={startPress}
            onTouchEnd={cancelPress}
            onTouchMove={cancelPress}
            onTouchCancel={cancelPress}
            className={`card relative flex flex-col hover:shadow-card-hover hover:-translate-y-0.5
                transition-[transform,box-shadow] duration-ui ease-out
                ${item.thrownAway ? 'opacity-60' : ''}
                ${selected ? 'ring-2 ring-primary-500' : ''}
                ${menuOpen ? 'z-30' : ''}`}>

            <a href={`/clothing/${item.id}`}
                data-testid="item-card-link"
                onClick={handleClick}
                onAuxClick={e => onAuxOpen(e, item.id)}
                className="flex flex-col flex-1 no-underline cursor-pointer rounded-[inherit]">

                {/* Фото — часть ссылки, а не отдельная нефункциональная картинка */}
                <div data-testid="item-card-photo"
                    className="relative aspect-[3/4] bg-gray-100 dark:bg-ink-700 overflow-hidden rounded-t-[inherit]">
                    {item.firstPhotoUrl ? (
                        <AuthImage imageKey={item.firstPhotoUrl} alt={item.name} variant="thumbnail"
                            className="w-full h-full object-cover"/>
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300 dark:text-gray-600">
                            <ShirtIcon className="w-12 h-12"/>
                        </div>
                    )}

                    {(item.thrownAway || item.inTrashBin) && (
                        <div className="absolute bottom-2 left-2">
                            {item.thrownAway ? (
                                <span className="badge bg-gray-900/80 text-gray-100 text-[10px]">
                                    <DisposalIcon className="w-3 h-3"/> {retirementLabel(item.lifecycleStatus) ?? 'Утилизировано'}
                                </span>
                            ) : (
                                <span className="badge badge-warning text-[10px]">
                                    <TrashIcon className="w-3 h-3"/> Ждёт передачи
                                </span>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex flex-col flex-1 p-3">
                    {/* Название в две строки: одна строка обрезала «Фланелевая рубашка …» до неузнаваемости (C2) */}
                    <h3 title={item.name}
                        className="font-semibold text-sm text-gray-900 dark:text-gray-100 line-clamp-2 leading-snug">
                        {item.name}
                    </h3>

                    {/* Мета-строка: «Категория · Бренд · Размер». Бренд — вход в каталог этого
                        бренда (CTHS-258), поэтому части рисуются раздельно, а не одной строкой.
                        Строка вырастает до 44 px на < lg только когда бренд кликабелен: иначе
                        каждая карточка без бренда платила бы за чужую тач-зону высотой. */}
                    {metaParts.length > 0 && (
                        <p data-testid="item-card-meta" title={meta}
                            className={`flex items-center mt-0.5 overflow-hidden whitespace-nowrap
                                text-xs text-gray-500 dark:text-gray-400
                                ${brandLinkId ? 'min-h-[44px] lg:min-h-0' : ''}`}>
                            {metaParts.map((part, index) => (
                                <React.Fragment key={`${part.isBrand}-${part.text}`}>
                                    {index > 0 && ' · '}
                                    {part.isBrand && brandLinkId
                                        ? brandLabel(part.text)
                                        : <span className="truncate">{part.text}</span>}
                                </React.Fragment>
                            ))}
                        </p>
                    )}

                    {item.conditionName && (
                        <div className="flex items-center gap-1.5 mt-1.5">
                            <span className={`w-2 h-2 rounded-full shrink-0 ${conditionDot(item.conditionName)}`}/>
                            <span className="text-xs text-gray-600 dark:text-gray-400 truncate">{item.conditionName}</span>
                        </div>
                    )}

                    {/* Нижняя строка: обе подписи в одну строку — на карточке 171 px «Не носили»
                        раньше ломалось пополам рядом с рейтингом (C7) */}
                    <div data-testid="item-card-stats"
                        className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700/50">
                        <span className="flex items-center gap-1.5 min-w-0 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">
                            {/* CTHS-266: давность носки цветом. Цвет не единственный носитель
                                смысла — рядом подпись носки, у точки title и доступное имя. */}
                            <span data-testid="item-card-wear-dot"
                                role="img"
                                aria-label={recency.title}
                                title={recency.title}
                                className={`w-2 h-2 rounded-full shrink-0 ${recency.dotClass}`}/>
                            <span className="truncate">
                                {item.totalWears ? `Носил ${pluralTimes(item.totalWears)}` : 'Не носили'}
                            </span>
                        </span>
                        {item.aiRatingOverall != null && (
                            /* Шкала обязательна в подписи: «★ 7.6» без «/10» не читалась (QA-15). */
                            <span className="text-xs text-amber-500 dark:text-amber-400 font-semibold tabular-nums whitespace-nowrap"
                                title="Оценка вещи по шкале от 0 до 10">
                                ★ {item.aiRatingOverall.toFixed(1)}/10
                            </span>
                        )}
                    </div>
                </div>
            </a>

            {/* Управление поверх фото: тач-зоны 44×44 (C7). Обёртка повторяет геометрию
                фотоблока, поэтому кнопки лежат на фото, а не на тексте, и не проваливаются
                в ссылку карточки. */}
            <div className="absolute inset-x-0 top-0 aspect-[3/4] pointer-events-none z-20">
                {selectionMode && selectable && (
                    <label data-testid="item-card-select"
                        className="pointer-events-auto absolute top-0 left-0 w-11 h-11 flex items-center justify-center cursor-pointer"
                        onClick={e => e.stopPropagation()}>
                        <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-gray-900/70 hover:bg-gray-900/90 transition-colors">
                            <input type="checkbox" checked={selected}
                                aria-label={`Выбрать: ${item.name}`}
                                onChange={() => onToggleSelect(item.id)}
                                className="w-4 h-4 rounded accent-primary-500 cursor-pointer"/>
                        </span>
                    </label>
                )}

                {/* Замороженной вещи (ждёт передачи/выбыла) избранное недоступно: бэкенд отвечает 409 */}
                {!item.thrownAway && !item.inTrashBin && (
                    <IconButton
                        label={item.favorite ? 'Убрать из избранного' : 'В избранное'}
                        data-testid="favorite-toggle"
                        aria-pressed={item.favorite === true}
                        className="pointer-events-auto absolute top-0 right-0 text-white"
                        onClick={e => onToggleFavorite(item.id, e)}
                    >
                        <span className="flex items-center justify-center w-7 h-7 rounded-full bg-gray-900/60 hover:bg-gray-900/80 transition-colors">
                            <BookmarkIcon className={`w-3.5 h-3.5 transition-colors ${item.favorite ? 'text-primary-400 fill-primary-400' : 'text-white'}`}/>
                        </span>
                    </IconButton>
                )}

                {/* ⋮ живёт на фото, а не в строке статистики: там он отнимал у подписей
                    половину ширины узкой карточки */}
                <IconButton
                    label="Действия с вещью"
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    data-testid="card-menu-button"
                    className="pointer-events-auto absolute bottom-0 right-0 text-white"
                    onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        onToggleMenu(item.id);
                    }}
                >
                    <span className="flex items-center justify-center w-7 h-7 rounded-full bg-gray-900/60 hover:bg-gray-900/80 transition-colors">
                        <EllipsisVerticalIcon className="w-4 h-4"/>
                    </span>
                </IconButton>
            </div>

            {menuOpen && (
                <>
                    <div className="fixed inset-0 z-10" aria-hidden onClick={onCloseMenu}/>
                    <div role="menu" aria-label={`Действия: ${item.name}`} data-testid="card-menu"
                        className="fw-popover origin-bottom-right absolute right-2 bottom-2 z-20 min-w-[200px] py-1
                            bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg">
                        <button role="menuitem" type="button"
                            onClick={() => onNavigate(item.id)}
                            className="w-full px-4 py-2 min-h-[44px] text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2">
                            <ShirtIcon className="w-4 h-4"/> Открыть
                        </button>
                        {canEdit && !item.thrownAway && !item.inTrashBin && (
                            <button role="menuitem" type="button" onClick={() => onWear(item.id)}
                                className="w-full px-4 py-2 min-h-[44px] text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2">
                                <ClockIcon className="w-4 h-4"/> Носил сегодня
                            </button>
                        )}
                        {/* «Стирал» (CTHS-264): счётчик ухода за вещью иначе не ведётся —
                            ради одной отметки никто не открывает карточку. */}
                        {canEdit && !item.thrownAway && !item.inTrashBin && (
                            <button role="menuitem" type="button" onClick={() => onWash(item.id)}
                                data-testid="card-menu-wash"
                                className="w-full px-4 py-2 min-h-[44px] text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2">
                                <DropletsIcon className="w-4 h-4"/> Стирал
                            </button>
                        )}
                        {canManageListItems && !item.thrownAway && !item.inTrashBin && (
                            <button role="menuitem" type="button" onClick={() => onAddToList(item.id)}
                                className="w-full px-4 py-2 min-h-[44px] text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2">
                                <ListIcon className="w-4 h-4"/> В список
                            </button>
                        )}
                        {canMoveToTrash && !item.thrownAway && !item.inTrashBin && (
                            <button role="menuitem" type="button" onClick={() => onTrash(item.id)}
                                className="w-full px-4 py-2 min-h-[44px] text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2">
                                <TrashIcon className="w-4 h-4"/> Решить расстаться
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default ClothingItemCard;
